/**
 * A fairly simple media playback component that keeps track of play state
 * as well as playback time. This is _almost_ a purely presentational component,
 * but we keep track of the seek state while the user is dragging the bar.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color} from "@khanacademy/wonder-blocks-tokens";

import pauseIcon from "@phosphor-icons/core/fill/pause-fill.svg";
import playIcon from "@phosphor-icons/core/fill/play-fill.svg";

// Get a constrained seek position that accounts for null/zero values
const getSeekPosition = (current: number, total: number) =>
    !(current || total) ? 0 : Math.min(1, Math.max(0, current / total));

// Get a reasonable CSS percentage value rounded to nearest 1/100th
const getCSSPercentage = (position: number) =>
    (Math.round(100 * 100 * position) / 100).toString() + "%";

// Get a time value string from milliseconds
const getTimeValue = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const displayMinutes = Math.floor(seconds / 60).toString();
    let displaySeconds = (seconds % 60).toString();
    if (displaySeconds.length === 1) {
        displaySeconds = "0" + displaySeconds;
    }
    return displayMinutes + ":" + displaySeconds;
};

type Props = {
    // Current point in playback (ms)
    currentTime: number;
    // A callback fired when the play/pause button is clicked
    onClickPlay: () => unknown;
    // A callback fired while we are seeking
    onSeek: (timeInSeconds: number) => unknown;
    // A callback fired when we finish seeking
    onSeekEnd: () => unknown;
    // A callback fired when we start seeking
    onSeekStart: () => unknown;
    // A class to apply to the play button (hack hack hack)
    playButtonClassName: string;
    // Whether we are currently playing
    playing: boolean;
    // Whether the LiveEditor has deemed the audio ready to play
    readyToPlay: boolean;
    // The total playback time
    totalTime: number;
    // Optional link to YouTube version
    youtubeUrl: string;
};

type State = {
    dragging: boolean;
    seekPosition: number | null | undefined;
};

class ScratchpadPlaybackBar extends React.Component<Props, State> {
    static defaultProps: {
        currentTime: number;
    } = {
        currentTime: 0,
    };

    state: State = {
        dragging: false,
        seekPosition: null,
    };

    handleSeekDown = (e: React.MouseEvent) => {
        // We're going to release on the next mouseUp event
        this.setState({dragging: true});
        document.addEventListener("mousemove", this.handleSeekMove);
        document.addEventListener("mouseup", this.handleSeekRelease);
        // Apply some CSS to the document body to prevent text selection
        // and to keep cursor: pointer while dragging
        document.body?.classList.add(css(bodyStyles.seeking));
        if (this.props.onSeekStart) {
            this.props.onSeekStart();
        }
        this.handleSeekMove(e);
    };

    handleSeekMove = (e: React.MouseEvent | MouseEvent) => {
        // eslint-disable-next-line react/no-string-refs
        const seek = this.refs.seek;
        if (!seek) {
            return;
        }

        // @ts-expect-error - TS2339 - Property 'getBoundingClientRect' does not exist on type 'ReactInstance'.
        const bounds = seek.getBoundingClientRect();
        const seekPosition = Math.max(
            Math.min(1, (e.clientX - bounds.left) / bounds.width),
            0,
        );
        this.setState({seekPosition});
        this.props.onSeek(seekPosition * this.props.totalTime);
    };

    handleSeekRelease: (e: MouseEvent) => void = (e: MouseEvent) => {
        document.removeEventListener("mousemove", this.handleSeekMove);
        document.removeEventListener("mouseup", this.handleSeekRelease);
        document.body?.classList.remove(css(bodyStyles.seeking));
        this.setState({
            dragging: false,
            seekPosition: null,
        });
        if (this.props.onSeekEnd) {
            this.props.onSeekEnd();
        }
    };

    render(): React.ReactNode {
        const {
            currentTime,
            onClickPlay,
            playing,
            playButtonClassName,
            readyToPlay,
            totalTime,
            youtubeUrl,
        } = this.props;
        const {dragging, seekPosition} = this.state;
        const seekPositionCSS = getCSSPercentage(
            seekPosition != null
                ? seekPosition
                : getSeekPosition(currentTime, totalTime),
        );
        return (
            <div className={css(styles.wrap, !readyToPlay && styles.waiting)}>
                <span className={playButtonClassName}>
                    <IconButton
                        icon={readyToPlay && playing ? pauseIcon : playIcon}
                        kind="tertiary"
                        aria-label={readyToPlay && playing ? t`Pause` : t`Play`}
                        onClick={onClickPlay}
                    />
                </span>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error */}
                <div
                    className={css(styles.seek)}
                    onMouseDown={this.handleSeekDown}
                    // eslint-disable-next-line react/no-string-refs
                    ref="seek"
                >
                    <div className={css(styles.seekInner)}>
                        <div
                            className={css(styles.seekProgress)}
                            style={{
                                width: seekPositionCSS,
                            }}
                        />
                    </div>
                    <div
                        className={css(
                            styles.seekHandle,
                            dragging && styles.seekHandleActive,
                        )}
                        style={{
                            left: seekPositionCSS,
                        }}
                    />
                </div>
                {!!totalTime && (
                    <div className={css(styles.time)}>
                        {getTimeValue(
                            seekPosition != null
                                ? seekPosition * totalTime
                                : currentTime,
                        )}
                        {"/"}
                        {getTimeValue(totalTime)}
                    </div>
                )}
                {youtubeUrl && (
                    <Button
                        aria-label={t`Watch this talkthrough on YouTube`}
                        href={youtubeUrl}
                        target="_blank"
                        kind="tertiary"
                        style={styles.linkToYT}
                    >
                        {t`Watch on YouTube`}
                    </Button>
                )}
            </div>
        );
    }
}

const seekHeight = 20;

const seekHandleActive = {
    marginLeft: -seekHeight / 4,
    width: seekHeight / 2,
} as const;

const bodyStyles = StyleSheet.create({
    // Applied to the body while we're seeking with the slider
    seeking: {
        cursor: "pointer",
        userSelect: "none",
    },
});

const styles = StyleSheet.create({
    linkToYT: {
        marginLeft: 4,
    },
    seek: {
        alignItems: "center",
        cursor: "pointer",
        display: "flex",
        flex: 1,
        height: seekHeight,
        marginLeft: 16,
        minWidth: 400,
        position: "relative",
        top: -2,
    },
    seekInner: {
        background: color.offBlack16,
        borderRadius: 2,
        height: "25%",
        width: "100%",
    },
    seekProgress: {
        background: color.blue,
        borderRadius: 2,
        height: "100%",
    },
    seekHandle: {
        background: color.blue,
        borderColor: "transparent",
        borderRadius: 2,
        borderWidth: seekHeight / 4,
        display: "block",
        height: "100%",
        marginLeft: -seekHeight / 8,
        position: "absolute",
        top: 0,
        width: seekHeight / 4,
        ":hover": {
            ...seekHandleActive,
        },
    },
    seekHandleActive: {
        ...seekHandleActive,
    },
    time: {
        fontSize: 16,
        marginLeft: 10,
        textAlign: "center",
        // Keep width constant even as time changes
        // This is good for two-digit numbers of minutes
        // Hopefully none of our talkthroughs are longer than that
        // That would make Sal pretty upset.
        width: 80,
    },
    waiting: {
        opacity: 0.5,
        pointerEvents: "none",
    },
    wrap: {
        alignItems: "center",
        display: "flex",
        justifyContent: "space-between",
        userSelect: "none",
    },
});

export default ScratchpadPlaybackBar;
